/**
 * MomentumBarRenderer — screen-space momentum bar with angular geometric shape,
 * glow halo, ghost trail drain visualization, shot tier threshold markers,
 * and numeric readout.
 *
 * Extracted from HUDRenderer to own all momentum bar Graphics objects and state.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { WORLD, MOMENTUM, SHOT_TIER, Z } from '../../constants';
import type { GameState } from '../../types';
import { modeUsesShotTiers } from '../../game/mode';
import { getUrgencyColor, getUrgencyPulse } from '../urgency';

export class MomentumBarRenderer {
  // Momentum bar (screen-space, bottom center)
  private momentumBarBorder: Graphics;
  private momentumBarFill: Graphics;
  private momentumBarGlow: Graphics;
  private momentumBarGhost: Graphics;
  private momentumBarTicks: Graphics;

  // Ghost trail state
  private ghostFraction: number = 0;
  private ghostTimer: number = 0;
  private prevMomentumFraction: number = 1;

  // Momentum bar upgrades
  private momentumReadoutText: Text;
  private momentumThresholdGfx: Graphics;
  private momentumThresholdLabels: Text[];

  constructor(uiContainer: Container) {
    // --- Momentum bar readout (screen-space, above bar) ---
    this.momentumReadoutText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: SHOT_TIER.READOUT_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0xffd700,
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this.momentumReadoutText.anchor.set(0.5, 1);
    this.momentumReadoutText.zIndex = Z.HUD_TICKS;
    this.momentumReadoutText.visible = false;
    uiContainer.addChild(this.momentumReadoutText);

    // --- Momentum threshold markers (screen-space, on bar) ---
    this.momentumThresholdGfx = new Graphics();
    this.momentumThresholdGfx.zIndex = Z.HUD_OVERLAY;
    this.momentumThresholdGfx.visible = false;
    uiContainer.addChild(this.momentumThresholdGfx);

    this.momentumThresholdLabels = [];
    for (let t = 0; t < 2; t++) {
      const thLabel = new Text({
        text: t === 0 ? 'S2' : 'S3',
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: SHOT_TIER.THRESHOLD_LABEL_SIZE,
          fontWeight: 'bold',
          fill: 0xffffff,
          stroke: { color: 0x000000, width: 2 },
        }),
      });
      thLabel.anchor.set(0.5, 1);
      thLabel.zIndex = Z.HUD_OVERLAY;
      thLabel.visible = false;
      uiContainer.addChild(thLabel);
      this.momentumThresholdLabels.push(thLabel);
    }

    // --- Momentum bar (screen-space, bottom center, angular shape) ---
    this.momentumBarGlow = new Graphics();
    this.momentumBarGlow.zIndex = Z.HUD_GLOW;
    this.momentumBarGlow.visible = false;
    uiContainer.addChild(this.momentumBarGlow);

    this.momentumBarBorder = new Graphics();
    this.momentumBarBorder.zIndex = Z.HUD_BASE;
    this.momentumBarBorder.visible = false;
    uiContainer.addChild(this.momentumBarBorder);

    this.momentumBarGhost = new Graphics();
    this.momentumBarGhost.zIndex = Z.HUD_BASE;
    this.momentumBarGhost.visible = false;
    uiContainer.addChild(this.momentumBarGhost);

    this.momentumBarFill = new Graphics();
    this.momentumBarFill.zIndex = Z.HUD_BASE;
    this.momentumBarFill.visible = false;
    uiContainer.addChild(this.momentumBarFill);

    this.momentumBarTicks = new Graphics();
    this.momentumBarTicks.zIndex = Z.HUD_TICKS;
    this.momentumBarTicks.visible = false;
    uiContainer.addChild(this.momentumBarTicks);

  }

  /** Hide all momentum bar elements (called by HUD during menu phases). */
  hide(): void {
    this.momentumBarBorder.visible = false;
    this.momentumBarFill.visible = false;
    this.momentumBarGlow.visible = false;
    this.momentumBarGhost.visible = false;
    this.momentumBarTicks.visible = false;
    this.momentumReadoutText.visible = false;
    this.momentumThresholdGfx.visible = false;
    for (const tl of this.momentumThresholdLabels) tl.visible = false;
  }

  /** Screen-space momentum bar — angular geometric shape with glow + ghost trail. */
  update(state: GameState, dt: number = 0): void {
    this.momentumBarBorder.clear();
    this.momentumBarFill.clear();
    this.momentumBarGlow.clear();
    this.momentumBarGhost.clear();
    this.momentumBarTicks.clear();
    this.momentumThresholdGfx.clear();

    const isDefense = state.phase === 'defensive_movement' && state.defenseActivePlayer >= 0;
    const isActive = state.phase === 'aiming' || state.phase === 'burst_window' || isDefense;
    const player = isDefense
      ? state.players[state.defenseActivePlayer]
      : state.players[state.currentPlayer];

    // Hide during air sub-phase (no resource to display) or when not in an active phase
    const isDefenseAir = isDefense && player?.defenseSubPhase === 'air';
    if (!isActive || !player?.alive || isDefenseAir) {
      this.momentumBarBorder.visible = false;
      this.momentumBarFill.visible = false;
      this.momentumBarGlow.visible = false;
      this.momentumBarGhost.visible = false;
      this.momentumBarTicks.visible = false;
      this.momentumReadoutText.visible = false;
      this.momentumThresholdGfx.visible = false;
      for (const tl of this.momentumThresholdLabels) tl.visible = false;
      return;
    }

    this.momentumBarBorder.visible = true;
    this.momentumBarFill.visible = true;
    this.momentumBarGlow.visible = true;
    this.momentumBarGhost.visible = true;
    this.momentumBarTicks.visible = true;

    const barW = MOMENTUM.BAR_WIDTH;
    const barH = MOMENTUM.BAR_HEIGHT;
    const cx = WORLD.CANVAS_WIDTH / 2;
    const barY = WORLD.CANVAS_HEIGHT - MOMENTUM.BAR_Y_OFFSET;
    const barX = cx - barW / 2;

    // Angular shape — angled ends (hex aesthetic, sharper cut)
    const bevel = barH * 0.6;

    // Fill fraction — landing timer during defense landing phase, momentum otherwise
    const fraction = isDefense
      ? (player.landingTimerMax > 0 ? Math.max(0, Math.min(1, player.landingTimer / player.landingTimerMax)) : 0)
      : (player.momentumTurnStart > 0 ? Math.max(0, Math.min(1, player.momentum / player.momentumTurnStart)) : 0);

    // Color based on fill level — defense uses player color tint
    const isLow = fraction < MOMENTUM.LOW_THRESHOLD;
    const isMid = fraction < MOMENTUM.MID_THRESHOLD;
    let fillColor: number;
    if (isDefense) {
      fillColor = isLow ? MOMENTUM.COLOR_LOW : player.color;
    } else {
      const hasBonus = player.momentumTurnStart > MOMENTUM.MAX;
      fillColor = isLow ? MOMENTUM.COLOR_LOW
        : isMid ? MOMENTUM.COLOR_MID
          : hasBonus ? MOMENTUM.COLOR_BONUS
            : MOMENTUM.COLOR_FULL;
    }

    // Low pulse — gentle alpha oscillation; urgency pulse during defense < 25%
    let barAlpha = 1;
    if (isDefense && fraction > 0 && fraction < 0.25) {
      barAlpha = getUrgencyPulse(fraction, Date.now() * 0.001);
      fillColor = getUrgencyColor(fraction);
    } else if (isLow && fraction > 0) {
      barAlpha = 0.7 + Math.sin(Date.now() * 0.008) * 0.3;
    }

    // Border (dark angular shape)
    const borderPad = 2;
    this.momentumBarBorder.poly([
      barX - borderPad + bevel, barY - borderPad,
      barX + barW + borderPad, barY - borderPad,
      barX + barW + borderPad - bevel, barY + barH + borderPad,
      barX - borderPad, barY + barH + borderPad,
    ]);
    this.momentumBarBorder.fill({ color: 0x1a1a2e, alpha: 0.9 });
    this.momentumBarBorder.poly([
      barX - borderPad + bevel, barY - borderPad,
      barX + barW + borderPad, barY - borderPad,
      barX + barW + borderPad - bevel, barY + barH + borderPad,
      barX - borderPad, barY + barH + borderPad,
    ]);
    this.momentumBarBorder.stroke({ color: fillColor, width: 2, alpha: 0.5 });

    // Ghost trail — translucent bar showing where momentum WAS 0.5s ago
    if (fraction < this.prevMomentumFraction - 0.001) {
      this.ghostFraction = this.prevMomentumFraction;
      this.ghostTimer = 0.5;
    }
    this.prevMomentumFraction = fraction;
    if (this.ghostTimer > 0 && dt > 0) {
      this.ghostTimer -= dt;
      const ghostAlpha = Math.max(0, this.ghostTimer / 0.5) * 0.3;
      if (ghostAlpha > 0.01 && this.ghostFraction > fraction) {
        const ghostW = barW * this.ghostFraction;
        const ghostBevel = bevel * this.ghostFraction;
        this.momentumBarGhost.poly([
          barX + bevel, barY,
          barX + ghostW, barY,
          barX + ghostW - ghostBevel, barY + barH,
          barX, barY + barH,
        ]);
        this.momentumBarGhost.fill({ color: fillColor, alpha: ghostAlpha });
      }
    }

    // Fill (angular, clipped to fraction)
    if (fraction > 0) {
      const fillW = barW * fraction;
      // Lerp bevel based on fill — left edge always has bevel, right edge only at full
      const rightBevel = bevel * fraction;
      this.momentumBarFill.poly([
        barX + bevel, barY,
        barX + fillW, barY,
        barX + fillW - rightBevel, barY + barH,
        barX, barY + barH,
      ]);
      this.momentumBarFill.fill({ color: fillColor, alpha: barAlpha });
    }

    // Momentum numeric readout (screen-space, above bar, shot-tier mode only during aiming)
    const showShotTierBar = !isDefense && modeUsesShotTiers(state.modeState.mode);
    if (showShotTierBar && state.phase === 'aiming') {
      this.momentumReadoutText.visible = true;
      this.momentumReadoutText.text = `${Math.round(player.momentum)}`;
      this.momentumReadoutText.style.fill = fillColor;
      this.momentumReadoutText.position.set(cx, barY - SHOT_TIER.READOUT_Y_ABOVE);

    } else {
      this.momentumReadoutText.visible = false;
    }

    // Hide threshold markers (no longer used — S2/S3 gated by ult meter)
    this.momentumThresholdGfx.visible = false;
    for (const tl of this.momentumThresholdLabels) tl.visible = false;

    // Glow halo — feathered concentric layers
    const glowAlpha = 0.05 + fraction * 0.08;
    const glowSteps = 3;
    for (let g = glowSteps; g >= 1; g--) {
      const gp = g * 3; // padding: 3, 6, 9
      const stepAlpha = (glowAlpha * barAlpha) / (g * 1.8); // outer = very faint
      const glowBevel = (barH + gp * 2) * 0.6;
      this.momentumBarGlow.poly([
        barX - gp + glowBevel, barY - gp,
        barX + barW + gp, barY - gp,
        barX + barW + gp - glowBevel, barY + barH + gp,
        barX - gp, barY + barH + gp,
      ]);
      this.momentumBarGlow.fill({ color: fillColor, alpha: stepAlpha });
    }
  }

  destroy(): void {
    this.momentumBarBorder.destroy();
    this.momentumBarFill.destroy();
    this.momentumBarGlow.destroy();
    this.momentumBarGhost.destroy();
    this.momentumBarTicks.destroy();
    this.momentumReadoutText.destroy();
    this.momentumThresholdGfx.destroy();
    for (const l of this.momentumThresholdLabels) l.destroy();
  }
}
